import { useState } from "react";
import toast from "react-hot-toast";
import { MdOutlinePersonAddAlt } from "react-icons/md";

import VendorPicker from "./VendorPicker";
import { useAddPaidVendors } from "../../hooks/paid-vendors";

/**
 * Side panel for granting access to one or more vendors in a single pass —
 * onboarding a batch of vendors is the common case, so the picker stays open
 * and accumulates a selection instead of closing after every pick.
 */
const AddPaidVendors = ({ onAdded, onCancel }) => {
  const [selected, setSelected] = useState([]);
  const { mutate: addPaidVendors, isPending } = useAddPaidVendors();

  const submit = () => {
    if (selected.length === 0) return;

    addPaidVendors(
      selected.map((vendor) => vendor._id),
      {
        onSuccess: (data) => {
          // Nothing was added when the picker was working off a stale list —
          // every pick was already listed, or the vendor has since been
          // deleted. Say so plainly instead of claiming success.
          if (data?.added?.length) toast.success(data.message);
          else toast(data?.message || "Nothing to add");
          onAdded();
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-1 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-primary8 text-primary flex items-center justify-center">
            <MdOutlinePersonAddAlt className="text-lg" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Add Paid Vendors</h2>
            <p className="text-xs text-gray-500">
              Paid vendors can see customer contact details on their enquiries.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-1">
        <VendorPicker selected={selected} onChange={setSelected} />
      </div>

      <div className="flex items-center justify-between gap-3 px-1 pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-500">
          {selected.length > 0
            ? `${selected.length} vendor${selected.length === 1 ? "" : "s"} selected`
            : "Pick at least one vendor"}
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={selected.length === 0 || isPending}
            className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Adding..." : "Add to paid list"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPaidVendors;
